import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Card = styled(motion.div)`
  position: relative;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: #ffffff;
  display: flex;
  flex-direction: column;
  
  .dark-mode & {
    background: #151d2e;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    
    .dark-mode & {
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
    }
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 220px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 200px;
  }
  
  @media (max-width: 480px) {
    height: 180px;
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    transparent 50%,
    rgba(0, 0, 0, 0.04) 100%
  );
  transition: background 0.4s ease;
  
  ${Card}:hover & {
    background: linear-gradient(
      to bottom,
      transparent 30%,
      rgba(0, 0, 0, 0.1) 100%
    );
  }
`;

const CategoryBadge = styled.span`
  position: absolute;
  top: 0.8rem;
  left: 0.8rem;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => props.$bgColor || 'var(--primary)'};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  z-index: 2;
  letter-spacing: 0.02em;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const CardContent = styled.div`
  padding: 1.2rem 1.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex: 1;
  
  @media (max-width: 480px) {
    padding: 1rem 1.1rem;
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--dark);
  margin: 0;
  line-height: 1.3;
  
  .dark-mode & {
    color: #ffffff;
  }
`;

const AccentLine = styled.div`
  width: 35px;
  height: 3px;
  border-radius: 2px;
  background-color: ${props => props.$color || 'var(--secondary)'};
  transition: width 0.3s ease;
  
  ${Card}:hover & {
    width: 60px;
  }
`;

const ProjectDescription = styled.p`
  font-size: 0.85rem;
  color: var(--gray-600);
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
  .dark-mode & {
    color: var(--gray-400);
  }
`;

const ProjectCard = ({ project, index, onClick, categoryColors }) => {
  // Déterminer la couleur d'accent en fonction de la catégorie
  const getCategoryColor = () => {
    switch(project.category) {
      case 'site-vitrine':
        return categoryColors?.siteVitrine || 'var(--black)';
      case 'e-commerce':
        return categoryColors?.ecommerce || 'rgba(255,255,255,0.12)';
      case 'application':
        return categoryColors?.application || 'var(--accent)';
      default:
        return 'var(--primary)';
    }
  };

  const accentColor = getCategoryColor();
  const categoryBg = categoryColors?.[project.category === 'site-vitrine' ? 'siteVitrine' : project.category === 'application' ? 'application' : 'ecommerce'] || getCategoryColor();
  
  const getCategoryName = () => {
    switch(project.category) {
      case 'site-vitrine':
        return 'Site Vitrine';
      case 'e-commerce':
        return 'E-commerce';
      case 'application':
        return 'Application Web';
      default:
        return project.category;
    }
  };
  
  const cardVariants = {
    hidden: { 
      opacity: 1, 
      y: 0 
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0, 
        delay: 0,
        ease: "easeOut"
      }
    }
  };
  
  const overlayVariants = {
    hover: {}
  };
  
  return (
    <Card
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      onClick={() => onClick(project)}
      whileHover="hover"
    >
      <ImageWrapper>
        <ProjectImage src={project.image} alt={project.title} />
        <ImageOverlay />
        <CategoryBadge $bgColor={categoryBg}>
          {getCategoryName()}
        </CategoryBadge>
      </ImageWrapper>
      <CardContent>
        <ProjectTitle>{project.title}</ProjectTitle>
        <AccentLine $color={accentColor} />
        <ProjectDescription>
          {project.description.substring(0, 100)}...
        </ProjectDescription>
      </CardContent>
    </Card>
  );
};

export default ProjectCard;
